import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute, SuperAdminRoute } from "@/components/common/protected-route";
import { RouteErrorBoundary } from "@/components/common/route-error-boundary";
import { paths } from "./paths";

const SignInPage = lazy(() => import("@/features/auth/SignInPage"));
const RecoverPasswordPage = lazy(() => import("@/features/auth/RecoverPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/features/auth/ResetPasswordPage"));
const HomePage = lazy(() => import("@/features/home/HomePage"));
const PaymentBatchesPage = lazy(
  () => import("@/features/payment-batches/PaymentBatchesPage"),
);
const PaymentBatchOrdersPage = lazy(
  () => import("@/features/payment-batches/PaymentBatchOrdersPage"),
);
const SimBankPage = lazy(() => import("@/features/sim-bank/SimBankPage"));
const SellersBalancesPage = lazy(
  () => import("@/features/sellers-balances/SellersBalancesPage"),
);
const SellerStatementPage = lazy(
  () => import("@/features/seller-statement/SellerStatementPage"),
);
const AutoWithdrawSellersPage = lazy(
  () => import("@/features/auto-withdraw/AutoWithdrawSellersPage"),
);

function PageFallback() {
  return <div className="text-sm text-muted-foreground">Carregando...</div>;
}

function lazyRoute(Component: React.LazyExoticComponent<React.FC>) {
  return (
    <Suspense fallback={<PageFallback />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    path: paths.SIGN_IN,
    element: lazyRoute(SignInPage),
  },
  {
    path: paths.RECOVER_PASSWORD,
    element: lazyRoute(RecoverPasswordPage),
  },
  {
    path: paths.RESET_PASSWORD,
    element: lazyRoute(ResetPasswordPage),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: paths.HOME,
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: lazyRoute(HomePage) },
          { path: paths.PAYMENT_BATCHES.slice(1), element: lazyRoute(PaymentBatchesPage) },
          {
            path: paths.PAYMENT_BATCH_ORDERS.slice(1),
            element: lazyRoute(PaymentBatchOrdersPage),
          },
          {
            path: paths.SELLERS_BALANCES.slice(1),
            element: lazyRoute(SellersBalancesPage),
          },
          {
            element: <SuperAdminRoute />,
            children: [
              { path: paths.SIM_BANK.slice(1), element: lazyRoute(SimBankPage) },
              {
                path: paths.SELLER_STATEMENT.slice(1),
                element: lazyRoute(SellerStatementPage),
              },
              {
                path: paths.AUTO_WITHDRAW.slice(1),
                element: lazyRoute(AutoWithdrawSellersPage),
              },
            ],
          },
          { path: "*", element: <Navigate to={paths.HOME} replace /> },
        ],
      },
    ],
  },
]);
